import {
  Avatar,
  cn,
  IconButton,
  TruncatedContent,
  useBoolean,
  useBreakpoints,
} from "@aurem/ui";
import { useNavigation } from "hooks/navigate";
import { ChevronRightIcon } from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { GetUserQuery } from "types";

type Props = {
  user: GetUserQuery["user"];
  onRoleSelect?: () => void;
};

type RoleItem = {
  id: string;
  text: string;
  type: "admin" | "employee";
  subtext?: string;
  companyId?: string;
};

// TODO: [Michael K] Add expandable low level component
export const RolePicker = ({ user, onRoleSelect }: Props) => {
  const { t } = useI18n();
  const { employmentId, companyId } = useParams();
  const { language } = useI18n();
  const navigate = useNavigation();
  const { isMobile } = useBreakpoints();
  const { value: openRoles, toggle: toggleRoles } = useBoolean(isMobile);
  const name = user.profile?.name;
  const email = user.email;

  const hasAdminRoles = user.userRoles?.some(
    (userRole) => userRole.role.isPlatform || userRole.company != null,
  );
  const hasEmployeeRoles = (user.employments?.length ?? 0) > 0;

  const roles: RoleItem[] = [
    ...(user.userRoles?.map((role) => ({
      id: role.id,
      text: role.role.name,
      subtext: role.company?.name,
      type: "admin" as const,
      companyId: role.company?.id,
    })) ?? []),
    ...(user.employments?.map((role) => ({
      id: role.id,
      text: t("apps.admin.common.employee"),
      subtext: role.company?.name,
      type: "employee" as const,
      companyId: role.company?.id,
    })) ?? []),
  ];

  const isRoleActive = useCallback(
    (role: RoleItem) => {
      if (role.type === "admin" && companyId) {
        return role.companyId === companyId;
      }

      if (role.type === "employee" && employmentId) {
        return role.id === employmentId;
      }

      return false;
    },
    [companyId, employmentId],
  );

  const handleRoleSelect = useCallback(
    (role: RoleItem) => {
      return () => {
        let url = "";
        if (role.type === "admin") {
          if (role.companyId) {
            url = `/${language}/admin/companies/${role.companyId}/dashboard/overview`;
          } else {
            url = `/${language}/admin/companies`;
          }
        } else {
          url = `/${language}/employee/overview`;
        }
        navigate(url);
        onRoleSelect?.();
      };
    },
    [navigate, language, onRoleSelect],
  );

  return (
    <div className="flex flex-col gap-4 px-4 pb-2 pt-4 sm:p-2 flex-1">
      <div className="flex gap-4 justify-between items-center flex-1">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <TruncatedContent className="text-md sm:text-sm font-medium">
            {name}
          </TruncatedContent>
          <TruncatedContent className="text-sm sm:text-xs text-neutral-500/50">
            {email}
          </TruncatedContent>
        </div>
        {!isMobile && hasAdminRoles && hasEmployeeRoles && (
          <div className="flex items-center justify-center shrink-0">
            <IconButton
              variant="ghost"
              color="neutral"
              size="sm"
              onClick={toggleRoles}
            >
              <ChevronRightIcon
                className={cn(
                  "rtl:-scale-x-100",
                  "transition-transform duration-150 motion-reduce:transition-none",
                  openRoles && "rotate-90",
                )}
              />
            </IconButton>
          </div>
        )}
      </div>
      {openRoles && (
        <div className="flex flex-col gap-2">
          {roles.map((role) => (
            <button
              key={role.id}
              className={cn(
                "flex px-2 py-2 rounded-xl w-full text-start",
                "border border-neutral-500/10 hover:bg-neutral-500/5 focus-visible:bg-neutral-500/5 active:bg-neutral-500/10",
                "focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
                isRoleActive(role) && "border-primary-500 bg-primary-500/5",
              )}
              style={{ outlineOffset: "1px" }}
              onClick={handleRoleSelect(role)}
            >
              <Avatar
                text={role.text}
                subtext={role.subtext}
                showDetails
                size={isMobile ? "md" : "sm"}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
